<template>
  <v-app
    :class="$vuetify.display.name"
    :theme="theme"
  >
    <v-app-bar
      flat
      :color="appColor"
    >
      <crv-app-title
        :allow-click="allowClickAppTitle"
        @click="handleClickAppTitle"
      >
        <v-icon 
          v-if="shouldShowNavBackInTitle"
          :icon="mdiArrowLeft" 
          @click.stop="handleClickBack"
        />
        <slot name="app-title" />
        <v-spacer />
      </crv-app-title>

      <!-- Desktop Account menu -->  
      <template #append>
        <crv-app-account-menu
          v-if="showAccountMenu"
          :account-label="accountLabel"
          :allow-switch-customer="allowSwitchCustomer"
          :customer-name="selectedCustomer?.name || customerName"
          :customer-number="selectedCustomer?.number || customerNumber"
          :faq-label="faqLabel"
          :is-my-crv="isMyCrv"
          :is-switch-customer-page="isSwitchCustomerPage"
          :locations="allowSwitchLocation ? internalLocations : []"
          :locations-label="locationsLabel"
          :location-number="selectedLocation?.id"
          :logout-label="logoutLabel"
          :market-country-code="marketCountryCode"
          :my-crv-label="myCrvLabel"
          :name="accountName"
          :select-customer-text="selectCustomerText"
          :select-customer-route="selectCustomerRoute"
          :select-all-locations-label="selectAllLocationsLabel"
          :show-my-crv-and-faq="showMyCrvAndFaq"
          :tooltip="tooltipAccountMenu"
          :tooltip-customer-switcher="tooltipCustomerSwitcher"
          :before-navigate-to-customer-switcher="beforeNavigateToCustomerSwitcher"
          :customer-name-override="accountMenuCustomerNameOverride"
          @logout="handleLogout"
          @switch-customer="handleSwitchCustomer"
          @click-location="handleClickLocation"
        />

        <v-divider
          class="mx-3 d-none d-sm-flex"
          vertical
        />
        
        <!-- Desktop Search app button -->
        <!-- TODO: not yet implemented, needs input from multiple teams -->
        <crv-search-app
          :show-search-app-button="showSearchButton"
          :tooltip="tooltipSearch" 
        />

        <!-- Desktop Language switcher -->
        <crv-app-locale-switcher
          v-if="showLocaleSwitcher"
          :locales="locales"
          :model-value="locale"
          :tooltip="tooltipLocale"
          @update:model-value="handleSwitchLocale"
        />

        <!-- Desktop Theme switcher button -->
        <Crv-app-theme-switcher
          :show-theme-switcher="showThemeSwitcher"
          :tooltip="tooltipThemeSwitcher"
          @update:theme="handleThemeUpdate"
        />
        
        <!-- Desktop Print button -->
        <crv-app-print 
          :show-print-button="showPrintButton"
          :tooltip="tooltipPrint"
          @click-print="handleClickPrint"
        />
    
        <!-- Desktop Help & Service button -->
        <crv-app-help-and-service 
          :show-help-and-service-button="showHelpAndServiceButton"
          :tooltip="tooltipHelpAndService"
          @click="handleClickHelpAndService"
        />
   
        <!-- Desktop App switcher button -->
        <crv-app-switcher
          v-if="showAppSwitcher"
          :apps-menu="appMenu"
          :hide-default-app-menu-items="hideDefaultAppMenuItems"
          :market-country-code="marketCountryCode"
          :tooltip="tooltipAppSwitcher"
        />



        <!-- Mobile more menu -->
        <v-btn
          :icon="mdiDotsVertical"
          class="d-sm-none"
        >
          <v-icon :icon="mdiDotsVertical" />

          <v-menu
            activator="parent"
          >
            <v-list slim>
              <!-- Mobile Account menu -->
              <crv-app-account-menu
                v-if="showAccountMenu"
                :account-label="accountLabel"
                :allow-switch-customer="allowSwitchCustomer"
                :customer-name="customerName"
                :faq-label="faqLabel"
                :is-my-crv="isMyCrv"
                :is-switch-customer-page="isSwitchCustomerPage"
                :locations="allowSwitchLocation ? internalLocations : []"
                :locations-label="locationsLabel"
                :location-number="selectedLocation?.id"
                :logout-label="logoutLabel"
                :market-country-code="marketCountryCode"
                :mobile-menu-mode="true"
                :my-crv-label="myCrvLabel"
                :name="accountName"
                :select-customer-text="selectCustomerText"
                :select-customer-route="selectCustomerRoute"
                :show-my-crv-and-faq="showMyCrvAndFaq"
                :tooltip="tooltipAccountMenu"
                @logout="handleLogout"
                @switch-customer="handleSwitchCustomer"
                @click-location="handleClickLocation"
              />

              <v-divider />
              
              <!-- Mobile-search-app -->
              <!-- TODO: not yet implemented, needs input from multiple teams -->
              <crv-search-app
                :mobile-menu-mode="true"
                :show-search-app-button="showSearchButton"
                :tooltip="tooltipSearch"
              />
              
              <!-- Mobile language switcher -->
              <crv-app-locale-switcher 
                v-if="showLocaleSwitcher"
                :locales="locales"
                :mobile-menu-mode="true"
                :model-value="locale"
                :tooltip="tooltipLocale"
                @update:model-value="handleSwitchLocale"
              />
              
              <!-- mobile-theme-switcher -->
              <Crv-app-theme-switcher
                :mobile-menu-mode="true"
                :show-theme-switcher="showThemeSwitcher"
                :tooltip="tooltipThemeSwitcher"
                @update:theme="handleThemeUpdate"
              />

              <!-- mobile-print-button -->
              <crv-app-print 
                :mobile-menu-mode="true"
                :show-print-button="showPrintButton"
                :tooltip="tooltipPrint"
                @click-print="handleClickPrint"
              />

              <!-- mobile-help-and-service-button -->
              <crv-app-help-and-service 
                :mobile-menu-mode="true"
                :show-help-and-service-button="showHelpAndServiceButton"
                :tooltip="tooltipHelpAndService"
                @click="handleClickHelpAndService"
              />

              <!-- mobile App switcher button -->
              <crv-app-switcher
                v-if="showAppSwitcher"
                :apps-menu="appMenu"
                :hide-default-app-menu-items="hideDefaultAppMenuItems"
                :market-country-code="marketCountryCode"
                :mobile-menu-mode="true"
                :tooltip="tooltipAppSwitcher"
              />
            </v-list>
          </v-menu>              
        </v-btn>
      </template>
    </v-app-bar>

    <crv-app-side-menu 
      v-if="showSideMenu"
      :active-menu-id="activeMenuId"
      :app-version="appVersion"
      :color="appColor"
      :is-collapsed="isCollapsed"
      :locale="locale"
      :location-number="locationNumber"
      :market-country-code="marketCountryCode"
      :menu-collapsed-label="menuCollapsedLabel"
      :menu-pinned-label="menuPinnedLabel"
      :permanent="$vuetify.display.mdAndUp"
      :refiner-main-form-id="refinerMainFormId"
      :refiner-project-id="refinerProjectId"
      :side-menu="sideMenu"
      :temporary="!$vuetify.display.mdAndUp"
      :user-email="userEmail"
      :user-id="userId"
      :user-name="userName"
      :user-roles="userRoles"
      @navigate="handleClickNavigate"
    >
      <slot 
        name="side-menu"
      />
    </crv-app-side-menu>
    <crv-app-bottom-menu
      v-if="showBottomMenu"
      :account-label="accountLabel"
      :account-name="accountName"
      :active-menu-id="activeMenuId"
      :allow-switch-customer="allowSwitchCustomer"
      :app-version="appVersion"
      :bottom-menu="bottomMenu"
      :customer-name="customerName"
      :customer-number="customerNumber"
      :customer-number-label="customerNumberLabel"
      :locale="locale"
      :locations="allowSwitchLocation ? internalLocations : []"
      :locations-label="locationsLabel"
      :location-number="locationNumber"
      :logout-label="logoutLabel"
      :market-country-code="marketCountryCode"
      :more-menu="moreMenu"
      :more-menu-label="moreMenuLabel"
      :refiner-main-form-id="refinerMainFormId"
      :refiner-project-id="refinerProjectId"
      :user-email="userEmail"
      :user-id="userId"
      :user-name="userName"
      :user-roles="userRoles"
      @navigate="handleClickNavigate"
      @logout="handleLogout"
      @switch-customer="handleSwitchCustomer"
      @click-location="handleClickLocation"
    />
    <v-main
      class="main-content"
      :class="classes"
    >
      <template v-if="isSelectCustomerRoute">
        <crv-select-customer
          :customers="customers"
          :fetch-customers="fetchCustomers"
          :customer-select-title="customerSelectTitle"
          :customer-name-label="customerNameLabel"
          :customer-number-label="customerNumberLabel"
          :customer-search-placeholder="customerSearchPlaceholder"
          :customer-no-data-label="customerNoDataLabel"
          :customers-per-page="customersPerPage"
          :minimum-search-length="customersMinimumSearchLength"
          :show-customers-pagination="showCustomersPagination"
          :customer-search-hint="customerSearchHint"
          @customer-selected="handleCustomerSelected"
        />
      </template>

      <template v-else>
        <slot />
      </template>
      <crv-app-footer 
        v-if="showFooter" 
        :theme="theme"
      />
    </v-main>
  </v-app>
</template>


<script>
import { mdiApps, mdiAccountCircle, mdiHelpCircle, mdiCog, mdiWeb, mdiArrowLeft, mdiSwapHorizontal, mdiMapMarker, mdiLogout, mdiMagnify, mdiClose, mdiTranslate, mdiWeatherSunny, mdiWeatherNight, mdiPrinter, mdiDotsVertical } from '@mdi/js'
import CrvAppAccountMenu from '@v3c/components/CrvAppAccountMenu.vue'
import CrvAppBottomMenu from '@v3c/components/CrvAppBottomMenu.vue'
import CrvAppFooter from '@v3c/components/CrvAppFooter.vue'
import CrvAppLocaleSwitcher from '@v3c/components/CrvAppLocaleSwitcher.vue'
import CrvAppSideMenu from '@v3c/components/CrvAppSideMenu.vue'
import CrvAppSwitcher from '@v3c/components/CrvAppSwitcher.vue'
import CrvAppTitle from '@v3c/components/CrvAppTitle.vue'
import CrvAppPrint from '@v3c/components/CrvAppPrint.vue'
import CrvSearchApp from '@v3c/components/CrvSearchApp.vue'
import CrvAppHelpAndService from './CrvAppHelpAndService.vue'
import CrvAppThemeSwitcher from '@v3c/components/CrvAppThemeSwitcher.vue'
import CrvSelectCustomer from '@v3c/components/CrvSelectCustomer.vue'


export default {
    name: 'CrvAppShell',

    components: {
        CrvAppTitle,
        CrvAppAccountMenu,
        CrvAppLocaleSwitcher,
        CrvAppSwitcher,
        CrvAppSideMenu,
        CrvAppBottomMenu,
        CrvAppFooter,
        CrvAppPrint,
        CrvSearchApp,
        CrvAppHelpAndService,
        CrvAppThemeSwitcher,
        CrvSelectCustomer
    },

    props: {
        shouldShowNavBackInTitle: {
            type: Boolean,
            default: true,
        },

        customers: {
            type: Array,
            default: () => [],
            note: `Optional static list of customers. 
            Used when you already have all customers available in memory.

            Example:
            [
              { id: 1, name: 'Customer A', number: '1234', ubn: '5678', locations: [
                { id: 1, name: "Farm Noord", address: "Noordweg 12, Utrecht" },
                { id: 2, name: "Farm Zuid", address: "Zuidlaan 88, Eindhoven" }
              ]}
            ]

            Note: If 'fetchCustomers' is also provided, it will be used instead of this list.
            `
        },

        fetchCustomers: {
            type: Function,
            default: undefined,
            note: `Optional async search function for fetching customers. 
            Takes precedence over the 'customers' prop when both are provided.

            Example:
            async (search) => {
              const result = await api.get('/customers', { params: { q: search } })
              return result.data
            }
            `
        },

        customerSelectTitle: {
            type: String,
            note: 'The title shown on the select customer page',
            default: "$t('appshell.selectCustomerTitle')",
        },

        customerNameLabel: {
            type: String,
            note: 'The label for the customer name column',
            default: "$t('appshell.customerNameLabel')",
        },

        customerNumberLabel: {
            type: String,
            note: 'The label for the customer number column',
            default: "$t('appshell.customerNumberLabel')",
        },

        customerSearchPlaceholder: {
            type: String,
            note: 'The placeholder for the customer search input',
            default: "$t('appshell.customerSearchPlaceholder')",
        },

        customerNoDataLabel: {
            type: String,
            note: 'The label shown when no customers match the search criteria',
            default: "$t('appshell.customerNoDataLabel')",
        },

        customersPerPage: {
            type: Number,
            note: 'The number of customers to show per page in the select customer table. Set to -1 to show all customers.',
            default: 10,
        },

        customersMinimumSearchLength: {
            type: Number,
            note: 'The minimum number of characters required to start searching customers. Set to 0 to disable this feature.',
            default: 0,
        },

        customerSearchHint: {
            type: String,
            note: 'The hint text shown below the customer search input',
            default: '',
        },

        showCustomersPagination: {
            type: Boolean,
            note: 'If true, pagination controls will be shown in the select customer table.',
            default: true,
        },

        allowClickAppTitle: {
            type: Boolean,
            default: true,
        },

        showAccountMenu: {
            type: Boolean,
            default: true,
        },

        allowSwitchCustomer: {
            type: Boolean,
            default: true,
        },

        accountLabel: {
            type: String,
            default: '$t("crvAppAccountMenuContent.loggedInAs")',
        },

        accountName: {
            type: String,
            default: undefined,
        },


        locations: {
            type: Array,
            default: () => [],
        },

        customerName: {
            type: String,
            default: undefined,
        },

        customerNumber: {
            type: String,
            default: '',
        },

        selectCustomerText: {
            type: String,
            default: '$t("crvAppAccountMenuContent.selectCustomer")',
        },

        selectCustomerRoute: {
            type: [Object, String],
            default: null,
        },

        selectAllLocationsLabel: {
            type: String,
            default: '$t("crvAppAccountMenuContent.selectAllLocations")',
        },

        isSwitchCustomerPage: {
            type: Boolean,
            default: false,
        },

        locale: {
            type: String,
            default: 'nl',
        },

        locales: {
            type: Array,
            default: () => [],
        },

        appMenu: {
            type: Object,
            default: undefined,
        },

        hideDefaultAppMenuItems: {
            type: Array,
            default: () => [],
            note: `An array of app IDs that should be hidden from the default menu. 
            This is useful if you want to customize the default menu for specific users or roles.
            Example: ['CrvAnimal', 'CrvAdmin'] will hide those apps from the default menu.`
        },

        marketCountryCode: {
            type: String,
            required: true,
        },

        menuPinnedLabel: {
            type: String,
            default: 'Pin Menu',
        },

        menuCollapsedLabel: {
            type: String,
            default: 'Hide Menu',
        },

        isCollapsed: {
            type: Boolean,
            default: false,
        },

        sideMenu: {
            type: Array,
            default: () => [],
        },

        locationsLabel: {
            type: String,
            default: 'Show data from',
        },

        myCrvLabel: {
            type: String,
            default: 'My CRV',
        },

        faqLabel: {
            type: String,
            default: 'Frequently asked questions',
        },

        logoutLabel: {
            type: String,
            default: 'Log out',
        },

        isMyCrv: {
            type: Boolean,
            default: false,
        },

        bottomMenu: {
            type: Array,
            default: () => [],
        },

        moreMenu: {
            type: Array,
            default: () => [],
            note: 'More menu for mobile mode',
        },

        moreMenuLabel: {
            type: String,
            default: 'More',
        },        

        activeMenuId: {
            type: String,
            default: '',
            note: 'The id (key) of the active menu item',
        },

        showLocaleSwitcher: {
            type: Boolean,
            default: true,
        },

        showAppSwitcher: {
            type: Boolean,
            default: true,
        },

        showSideMenu: {
            type: Boolean,
            default: true,
        },

        showMyCrvAndFaq: {
            type: Boolean,
            default: true,
        },

        showBottomMenu: {
            type: Boolean,
            default: true,
        },

        showFooter: {
            type: Boolean,
            default: true,
        },

        showThemeSwitcher: {
            type: Boolean,
            default: true,
        },

        showHelpAndServiceButton: {
            type: Boolean,
            default: true,
        },
        
        showSearchButton: {
            type: Boolean,
            default: false,
        },

        showPrintButton: {
            type: Boolean,
            default: false,
        },

        tooltipAppSwitcher: {
            type: String,
            default: ''
        },

        tooltipLocale: {
            type: String,
            default: ''
        },

        tooltipSearch: {
            type: String,
            default: 'Search through CRV apps', 
        }, // TODO: change default text to something like `$t('app-shell.tooltip.search') to let developers know to add translations.`

        tooltipPrint: {
            type: String,
            default: 'Print',
        },

        tooltipAccountMenu: {
            type: String,
            default: 'Account menu',
        },

        tooltipCustomerSwitcher: {
            type: String,
            default: 'Switch customer',
        },
        
        tooltipHelpAndService: {
            type: String,
            default: 'Help & Service',
        },

        tooltipThemeSwitcher: {
            type: String,
            default: 'Switch theme',
        },

        refinerProjectId: {
            type: String,
            default: '',
            note: `The Refiner project-Id for feedback collection. 
            If projectId and mainFormId are present, the appSideMenu and mobile BottomMenu will initiate refiner-buttons.`,
        },

        refinerMainFormId: {
            type: String,
            default: '',
            note: `The Refiner main-form-Id for feedback collection. 
            If projectId and mainFormId are present, the appSideMenu and mobile BottomMenu will initiate refiner-buttons.`,
        },

        appVersion: {
            type: String,
            default: '?.?.?', // TODO: make dynamic later
            note: `The app version, used for feedback collection in Refiner.`,
        },

        userRoles: {
            type: Array,
            default: () => [], // ['admin'] // TODO: make dynamic later, get from keycloak if possible
            note: `The user roles, used for feedback collection in Refiner.`,
        },

        userId: {
            type: String,
            default: 'anonymous', // TODO: make dynamic later, get from keycloak if possible
            note: `The app version, used for feedback collection in Refiner.`,
        },

        userName: {
            type: String,
            default: 'anonymous', // TODO: make dynamic later, get from keycloak if possible
            note: `The users's name, used for feedback collection in Refiner.`,
        },

        userEmail: {
            type: String,
            default: 'anonymous@crv4all.com', // TODO: make dynamic later, get from keycloak if possible
            note: `The user's e-mail, used for feedback collection in Refiner.`,
        },

        locationNumber: {
            type: String,
            default: '???', // TODO: make dynamic later, get from keycloak if possible
            note: `The location number, used for feedback collection in Refiner.`,
        },

        allowSwitchLocation: {
          type: Boolean,
          default: true,
          note: `If true, the location switcher will be shown in the account menu if multiple locations are available.`,
        },

        beforeNavigateToCustomerSwitcher: {
          type: Function,
          required: false,
          default: undefined,
          note: `Optional async callback function that will be called when the user clicks the Switch Customer option in the
          user menu. This function is of type () => boolean, the return value determining whether navigating is allowed.`
        },

        accountMenuCustomerNameOverride: {
          type: String,
          required: false,
          default: undefined,
          note: `Overrides the subtitle in the account menu button. Use sparingly`
        }
    },

    emits: [
        'click-app-title',
        'switch-locale',
        'navigate',
        'logout', 
        'switch-customer',
        'click-location',
        'click-back',
        'click-help-and-service',
        'click-print',
        'update:locations',
        'update:locationNumber',
    ],

    data () {
    return {
            theme: localStorage.getItem('crvThemeDark') === 'true' ? 'crvThemeDark' : 'crvThemeLight',
            selectedCustomer: null,
            selectedLocation: null,
            internalLocations: [],
            mdiApps,
            mdiHelpCircle,
            mdiAccountCircle,
            mdiCog,
            mdiWeb,
            mdiArrowLeft,
            mdiSwapHorizontal,
            mdiMapMarker,
            mdiLogout,
            mdiMagnify,
            mdiClose,
            mdiTranslate,
            mdiWeatherSunny,
            mdiWeatherNight,
            mdiPrinter,
            mdiDotsVertical,
        }
    },

    computed: {
        classes() {
            return {
                'has-footer': this.showFooter,
            }
        },

        appColor() {
          return this.theme === 'crvThemeLight' ? 'base-brand-blue' : 'primary-darken-1'
        },

        isSelectCustomerRoute() {
          return this.$route?.name === 'select-customer'
        },
    },

    created() {
        const isDarkTheme = localStorage.getItem('dark') === 'true'
        this.theme = isDarkTheme ? 'crvThemeDark' : 'crvThemeLight'
        this.$vuetify.theme.dark = isDarkTheme

        // TODO: implement if menu needs to be expanded 
        // const isMenuExpanded = localStorage.getItem('menuExpanded') === 'true'
        // this.menuExpanded = isMenuExpanded
    },  

    methods: {
        handleCustomerSelected(customer) {
          this.selectedCustomer = customer

          const hasMultiple = (customer.locations ?? []).length > 1

          this.internalLocations = (customer.locations ?? []).map((loc, idx) => ({
            id: loc.id,
            label: loc.name ?? `Location ${idx + 1}`,
            // Only mark single-location customers as selected; do NOT mark all when multiple.
            isSelected: !hasMultiple && idx === 0,
          }))

          // selectedLocation = null equals selected-all-locations
          this.selectedLocation = hasMultiple ? null : this.internalLocations[0] || null

          const selectedIds = hasMultiple ? [] : (this.internalLocations.length ? [this.internalLocations[0].id] : [])
          this.$emit('switch-customer', { ...customer, selectedLocations: selectedIds })

          const returnPath = sessionStorage.getItem('returnAfterCustomerSelect')
          if (returnPath) {
            sessionStorage.removeItem('returnAfterCustomerSelect')
            this.$router.push(returnPath)
          } else {
            this.$router.push({ path: '/' })
          }
        },

        handleClickAppTitle() {
            this.$emit('click-app-title')
        },

        handleSwitchLocale(locale) {
            this.$emit('switch-locale', locale)
        },

        handleClickNavigate(item) {
            this.$emit('navigate', item)
        },

        handleLogout() {
            this.$emit('logout')
        },

        handleSwitchCustomer() {
            if (this.allowSwitchCustomer) {
                this.$router.push({ name: 'select-customer' })
                // Inject the vue-3-components/routes directly into consuming apps router.ts!
            }
        },


        handleClickLocation(locationIdOrArray) {
          const ids = Array.isArray(locationIdOrArray) ? locationIdOrArray : [locationIdOrArray]

          if (!this.internalLocations || !this.internalLocations.length) return

          if (ids.length === this.internalLocations.length) {
            this.internalLocations = this.internalLocations.map(loc => ({ ...loc, isSelected: false }))
            this.selectedLocation = null
          } else {
            this.internalLocations = this.internalLocations.map(loc => ({
              ...loc,
              isSelected: ids.includes(loc.id),
            }))

            this.selectedLocation = ids.length === 1
              ? this.internalLocations.find(l => l.isSelected) || null
              : null
          }

          this.$emit('click-location', ids.length === 1 ? ids[0] : ids)
        },

        handleClickBack() {
            this.$emit('click-back')
        },

        handleClickHelpAndService() {
          this.$emit('click-help-and-service')
        },

        handleClickPrint() {
          this.$emit('click-print')
        },

        handleThemeUpdate(newTheme) {
          this.theme = newTheme
        }
    }
}
</script>


<style lang="scss" scoped>
@use '@v3c/assets/styles/variables.scss' as *;

    .main-content {
        min-height: calc(100vh - #{$desktop-header-height + $vertical-spacing-quadruple + 1px});

        &.has-footer {
            min-height: calc(100vh - #{$desktop-header-height + $desktop-footer-height + $vertical-spacing-quadruple + 1px});
        }
    }

</style>

